import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import '../styles/Quiz.css';
import LoadingSpinner from './LoadingSpinner';

const Quiz = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [userResponses, setUserResponses] = useState([]);

  // Función de aleatorización (Fisher-Yates)
  const shuffleArray = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Mapa de respuestas barajadas: { índicePregunta: [respuestasBarajadas] }
  // Se genera una sola vez por pregunta para que el orden no cambie al re-renderizar
  const [shuffledAnswersMap, setShuffledAnswersMap] = useState({});

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);

        const normalizeQuestion = (q) => {
          if (!q) return q;
          const preguntaText = q.pregunta || q.question || '';
          const explicacionText = q.explicacion || q.explanation || '';
          let respuestasMap = {};

          if (q.respuestas && typeof q.respuestas === 'object' && !Array.isArray(q.respuestas)) {
            respuestasMap = q.respuestas;
          } else if (Array.isArray(q.respuestas)) {
            q.respuestas.forEach((resp, idx) => {
              if (typeof resp === 'string') {
                respuestasMap[idx] = { id: idx, respuesta: resp, correcta: false };
              } else if (resp && typeof resp === 'object') {
                const respId = resp.id !== undefined ? resp.id : idx;
                respuestasMap[respId] = {
                  id: respId,
                  respuesta: resp.respuesta || resp.text || '',
                  correcta: resp.correcta === true || resp.correcta === 1 || String(resp.correcta) === "1" || String(resp.correcta).toLowerCase() === "true"
                };
              }
            });
          } else {
            const opcionesArray = q.opciones || q.options || [];
            if (Array.isArray(opcionesArray) && opcionesArray.length > 0) {
              const correctIdx = q.correctIndex !== undefined ? q.correctIndex : (q.correctAnswer !== undefined ? q.correctAnswer : 0);
              opcionesArray.forEach((opt, idx) => {
                respuestasMap[idx] = {
                  id: idx,
                  respuesta: opt,
                  correcta: idx === parseInt(correctIdx)
                };
              });
            }
          }

          return {
            ...q,
            pregunta: preguntaText,
            explicacion: explicacionText,
            respuestas: respuestasMap
          };
        };


        const isNumeric = /^\d+$/.test(quizId);
        const isSpecialty = String(quizId).startsWith('specialty~~');

        if (isNumeric || isSpecialty) {
          // ----------------- OFICIAL (Realtime DB) o ESPECIALIDAD -----------------
          const { getDatabase, ref, get } = await import('firebase/database');
          const { app } = await import('../firebase');

          const database = getDatabase(app);
          let preguntasList = [];

          if (isSpecialty) {
            // ID Format: specialty~~${hierarchyCode}~~${corpsCode}~~${specialtyCode}~~${temaId}~~${quizzId}
            const parts = quizId.split('~~');
            const hierarchy = parts[1];
            const corps = parts[2];
            const specialty = parts[3];
            const temaId = parts[4];
            const quizzId = parts[5];

            const path = `especialidades_temas/${hierarchy}/${corps}/${specialty}/temas/${temaId}/cuestionarios/${quizzId}`;
            console.log(`[Quiz] 🎓 Cargando cuestionario de especialidad RTDB: ${path}`);
            const snapshot = await get(ref(database, path));

            if (snapshot.exists()) {
              const quizzData = snapshot.val();
              const preguntasRaw = quizzData.preguntas || [];
              const preguntasArray = Array.isArray(preguntasRaw)
                ? preguntasRaw
                : Object.entries(preguntasRaw).map(([id, p]) => ({ id, ...p }));

              preguntasList = preguntasArray.filter(Boolean).map((p, idx) => normalizeQuestion({
                id: p.id || idx,
                ...p
              }));
            }
          } else {
            const temaId = parseInt(quizId, 10);
            
            // ESTRATEGIA DUAL: Primero intentar en el nuevo nodo optimizado
            const pathOptimized = `preguntas_por_tema/${temaId}`;
            const snapshotOptimized = await get(ref(database, pathOptimized));
            
            if (snapshotOptimized.exists()) {
              console.log(`[Quiz] 🚀 Cargando desde nuevo nodo optimizado: ${pathOptimized}`);
              const data = snapshotOptimized.val();
              preguntasList = Object.entries(data).map(([id, p]) => normalizeQuestion({
                id: parseInt(id),
                ...p
              }));
            } else {
              console.log(`[Quiz] 📡 Nodo optimizado vacío. Fallback a nodo legacy: preguntas`);
              const preguntasRef = ref(database, 'preguntas');
              const snapshotLegacy = await get(preguntasRef);
              
              if (snapshotLegacy.exists()) {
                const preguntasData = snapshotLegacy.val();
                const preguntasArray = Object.entries(preguntasData).map(([id, pregunta]) => ({
                  id: parseInt(id),
                  ...pregunta
                }));

                preguntasList = preguntasArray
                  .filter(q => q && q.tema_id === temaId)
                  .map(normalizeQuestion);
              }
            }
          }

          if (!preguntasList.length) {
            throw new Error('No hay preguntas disponibles para este cuestionario');
          }
          
          setQuestions(preguntasList);


        } else {
          // ----------------- PERSONAL / IMPORTADO (Firestore) -----------------
          if (!auth.currentUser) throw new Error('Debes iniciar sesión para ver tus cuestionarios');

          console.log(`[Quiz] 📂 Cargando cuestionario personal: ${quizId}`);
          const questionsRef = collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quizId, 'questions');
          const snapshot = await getDocs(questionsRef);
          console.log(`[Quiz] Snapshot size: ${snapshot.size}, empty: ${snapshot.empty}`);

          if (!snapshot.empty) {
            const questionsList = snapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .map(normalizeQuestion);

            console.log(`[Quiz] Normalized questions list:`, questionsList);
            setQuestions(questionsList);
          } else {
            throw new Error('No se encontraron preguntas para este cuestionario personalizado.');
          }
        }

        setError('');

      } catch (err) {
        console.error('Error cargando preguntas:', err);
        setError(err.message || 'Error al cargar las preguntas');
      } finally {
        setLoading(false);
      }
    };
    if (quizId) fetchQuestions();
  }, [quizId]);

  // Cuando cambien las preguntas, generar el mapa de respuestas barajadas
  useEffect(() => {
    if (questions.length > 0) {
      const map = {};
      questions.forEach((q, idx) => {
        const respuestas = Array.isArray(q.respuestas)
          ? q.respuestas
          : (q.respuestas ? Object.values(q.respuestas) : []);
        map[idx] = shuffleArray(respuestas.filter(Boolean));
      });
      setShuffledAnswersMap(map);
    }
  }, [questions]);

  const playSound = (isCorrect) => {
    const soundPath = isCorrect ? '/sounds/correct_sound.mp3' : '/sounds/incorrect_sound.mp3';
    const audio = new Audio(soundPath);
    audio.play().catch(err => console.warn("Interacción de audio bloqueada por el navegador:", err));
  };

  const handleAnswer = (respuestaId, isCorrect) => {
    if (selectedAnswer !== null) return; // Evitar múltiples selecciones
    
    setSelectedAnswer(respuestaId);
    playSound(isCorrect); 

    const currentQuestionData = questions[currentQuestion];
    const newResponses = [...userResponses, {
      questionId: currentQuestionData.id,
      questionText: currentQuestionData.pregunta,
      selectedAnswerId: respuestaId,
      isCorrect: isCorrect,
      correctAnswer: Object.values(currentQuestionData.respuestas).find(r => r.correcta)?.respuesta || 'Desconocida'
    }];
    setUserResponses(newResponses);

    if (isCorrect) setScore(score + 1);
    
    // Ya no saltamos automáticamente en 800ms para permitir leer la retroalimentación
    // Si la pregunta no tiene explicación, podríamos saltar en 2 segundos
    if (!currentQuestionData.explicacion) {
        setTimeout(() => {
            if (selectedAnswer === null) handleNext(); // Solo si el usuario no ha pulsado "Siguiente" manualmente
        }, 2500);
    }
  };

  const handleNext = () => {
    if (currentQuestion + 1 < questions.length) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
    } else {
      navigate('/dashboard/quiz-results', {
        state: {
          score: score,
          totalQuestions: questions.length,
          quizId,
          quizType: 'especificos',
          responses: userResponses
        }
      });
    }
  };

  const handlePrev = () => {
    if (currentQuestion <= 0) return;
    const prevQuestionData = questions[currentQuestion - 1];
    const prevResponse = userResponses.find(r => r.questionId === prevQuestionData.id);
    setCurrentQuestion(currentQuestion - 1);
    setSelectedAnswer(prevResponse?.selectedAnswerId || null);
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setUserResponses([]);
  };

  if (loading) return <div className="quiz-container"><div className="loading">Cargando preguntas...</div></div>;
  if (error) return <div className="quiz-container"><div className="error-message">{error}</div></div>;

  const pregunta = questions[currentQuestion];
  if (!pregunta) return <div className="quiz-container"><div className="error-message">Error: Pregunta no encontrada</div></div>;

  // Usar respuestas barajadas para la pregunta actual
  const respuestas = (shuffledAnswersMap[currentQuestion] || (
    Array.isArray(pregunta.respuestas)
      ? pregunta.respuestas
      : Object.values(pregunta.respuestas)
  )).filter(Boolean);

  return (
    <div className="quiz-container">
      <div className="quiz-header">
        <button 
          className="back-button" 
          onClick={() => navigate('/dashboard/quiz', { state: { selectedArea: location.state?.areaId } })}
        >
          ← Volver
        </button>
        <div className="quiz-stats">
          <div>Pregunta {currentQuestion + 1} / {questions.length}</div>
          <div>Puntaje: {score}</div>
        </div>
      </div>
      <div className="question-container" key={currentQuestion}>
        <div className="question-text">{pregunta.pregunta}</div>
        <div className="options-grid">
          {respuestas.map((r, idx) => {
            const isSelected = selectedAnswer === r.id;
            const isCorrect = r.correcta;
            let buttonClass = "answer-button";
            
            if (selectedAnswer !== null) {
              if (isCorrect) {
                buttonClass += " correct"; // Siempre verde si es la correcta
              } else if (isSelected) {
                buttonClass += " incorrect"; // Roja solo si es la seleccionada y es errónea
              }
            }

            return (
              <button
                key={`${currentQuestion}-${idx}`}
                className={buttonClass}
                onClick={() => handleAnswer(r.id, isCorrect)}
                disabled={selectedAnswer !== null}
              >
                {r.respuesta}
              </button>
            );
          })}
        </div>

        {selectedAnswer !== null && (
          <div className="feedback-section animate-fade-in">
            <div className={`feedback-banner ${respuestas.find(r => r.id === selectedAnswer)?.correcta ? 'correct' : 'incorrect'}`}>
              {respuestas.find(r => r.id === selectedAnswer)?.correcta 
                ? '✨ ¡Excelente! Respuesta correcta.' 
                : '❌ Respuesta incorrecta. Sigue estudiando.'}
            </div>
            
            {pregunta.explicacion && (
              <div className="explanation-card">
                <div className="explanation-title">💡 Retroalimentación</div>
                <div className="explanation-content">{pregunta.explicacion}</div>
              </div>
            )}
            
            <button className="next-btn-large" onClick={handleNext}>
              {currentQuestion + 1 < questions.length ? 'Siguiente Pregunta →' : 'Ver Resultados Finales 📊'}
            </button>
          </div>
        )}
      </div>
      <div className="navigation-buttons">
        <button className="nav-button" onClick={handlePrev} disabled={currentQuestion === 0}>
          ← Anterior
        </button>
        <button
          className={currentQuestion + 1 >= questions.length ? 'finish-button' : 'nav-button'}
          onClick={handleNext}
          disabled={selectedAnswer === null}
        >
          {currentQuestion + 1 < questions.length ? 'Siguiente →' : 'Finalizar 📊'}
        </button>
      </div>
    </div>
  );
};

export default Quiz;